import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useScript } from 'hooks/useScript.js'
import { localeForExternalService } from 'src/common/utils/locale'
import { watchGlobalVariable } from 'src/core/init/watch-global/index.js'
import getLppToken from '@/apollo/queries/getLppToken.gql'
import { useLazyQuerySmart } from '@/apollo/useLazyQuerySmart.js'

const pokerData = ref(null)
const pokerSession = computed(() => pokerData.value?.token ?? null)
const loading = ref(false)
const loadingError = ref(false)
const runtimeReady = ref(false)
const runtimeScriptError = ref(null)
const options = ref({
  theme: {
    style: 'light',
  },
})

let initPromise = null
let runtimePromise = null
let pokerScriptLoadKey = 0
let pokerPreflightData = null

export function setPokerPreflightData(payload) {
  pokerPreflightData = payload
}

export function getPokerPreflightData() {
  return pokerPreflightData
}

export function clearPokerPreflightData() {
  pokerPreflightData = null
}

function takePokerPreflightData() {
  const payload = pokerPreflightData
  clearPokerPreflightData()
  return payload
}

function getPokerRetryDelay() {
  const minDelayMs = 1000
  const maxDelayMs = 5000

  return Math.floor(minDelayMs + Math.random() * (maxDelayMs - minDelayMs))
}

function removePokerScripts(src) {
  const targetUrl = new URL(src, window.location.origin)
  targetUrl.searchParams.delete('cb')

  document.querySelectorAll('script[src]').forEach((script) => {
    const scriptSrc = script.getAttribute('src')

    if (!scriptSrc) {
      return
    }

    const scriptUrl = new URL(scriptSrc, window.location.origin)
    scriptUrl.searchParams.delete('cb')

    if (scriptUrl.href === targetUrl.href) {
      script.remove()
    }
  })
}

async function ensurePokerRendererReady({ forceScriptReload = false } = {}) {
  const src = process.env.POKER_SCRIPT_SRC

  if (!src) {
    throw new Error('Poker script src is not configured')
  }

  const scriptOptions = {
    async: true,
    defer: false,
    module: process.env.POKER_SCRIPT_MODULE === 'true',
  }
  const loadKey = String((pokerScriptLoadKey += 1))
  const attempts = 3
  let lastError = null

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const shouldReloadScript = forceScriptReload || attempt > 0

    if (shouldReloadScript) {
      removePokerScripts(src)
      delete window.lpp
      delete window.LppRender
    }

    let scriptSrc = src

    if (shouldReloadScript) {
      const scriptUrl = new URL(src, window.location.origin)
      scriptUrl.searchParams.set('cb', `${loadKey}_${attempt}`)
      scriptSrc = src.startsWith('http') ? scriptUrl.href : `${scriptUrl.pathname}${scriptUrl.search}${scriptUrl.hash}`
    }

    try {
      if (window.LppRender) {
        return true
      }

      const { load } = useScript(scriptSrc, scriptOptions)
      const isScriptLoaded = await load()

      if (!isScriptLoaded) {
        removePokerScripts(src)
        lastError = new Error('Poker runtime script failed to load')
      } else {
        let rendererReadyTimeoutId = null

        try {
          await Promise.race([
            watchGlobalVariable('LppRender'),
            new Promise((_, reject) => {
              rendererReadyTimeoutId = setTimeout(() => {
                reject(new Error('Poker runtime global "LppRender" was not ready in time'))
              }, 10000)
            }),
          ])
        } finally {
          clearTimeout(rendererReadyTimeoutId)
        }

        return true
      }
    } catch (error) {
      removePokerScripts(src)
      lastError = error
    }

    if (attempt < attempts - 1) {
      await new Promise((resolve) => {
        setTimeout(resolve, getPokerRetryDelay())
      })
    }
  }

  throw lastError
}

async function preparePokerRuntimeScript(options = {}) {
  try {
    await ensurePokerRendererReady(options)
    runtimeScriptError.value = null

    return true
  } catch (error) {
    runtimeReady.value = false
    runtimeScriptError.value = error
    throw error
  }
}

function shouldInitRuntime(lppInit) {
  return Boolean(lppInit?.token && (!lppInit?.statusCode || lppInit.statusCode === 'OK'))
}

export function usePoker() {
  const { locale } = useI18n()

  const optionsResult = computed(() => {
    return {
      ...options.value,
      lang: localeForExternalService(locale.value),
      uuid: pokerSession.value,
    }
  })

  const { fetch: init, onResult } = useLazyQuerySmart(getLppToken)

  function updatePokerData(lppInit) {
    if (!lppInit) {
      return
    }

    pokerData.value = lppInit
  }

  onResult(({ data }) => {
    updatePokerData(data?.lppInit)
  })

  async function createPokerRuntime({ forceScriptReload = false } = {}) {
    if (!pokerSession.value) {
      return false
    }

    if (!runtimePromise) {
      runtimeReady.value = false

      runtimePromise = (async () => {
        try {
          if (!window.lpp || forceScriptReload) {
            await preparePokerRuntimeScript({ forceScriptReload })
            window.lpp = new window.LppRender(optionsResult.value)
          } else {
            window.lpp.updateOptions(optionsResult.value)
          }

          window.lpp.init()
          runtimeReady.value = true
          runtimeScriptError.value = null

          return true
        } catch (error) {
          runtimeReady.value = false
          runtimeScriptError.value = error
          throw error
        }
      })().finally(() => {
        runtimePromise = null
      })
    }

    return runtimePromise
  }

  async function initPoker(initOptions = {}) {
    const { loadRuntime = true, forceRuntimeScriptReload = false } = initOptions

    if (initPromise) {
      const lppInit = await initPromise

      if (loadRuntime && shouldInitRuntime(lppInit) && (!runtimeReady.value || forceRuntimeScriptReload)) {
        await createPokerRuntime({ forceScriptReload: forceRuntimeScriptReload })
      }

      return lppInit
    }

    loading.value = true
    loadingError.value = false
    runtimeScriptError.value = null

    initPromise = (async () => {
      try {
        const preflightData = takePokerPreflightData()
        const response = preflightData ? null : await init(null, { fetchPolicy: 'network-only' })
        const lppInit = preflightData ?? response?.lppInit

        updatePokerData(lppInit)

        if (loadRuntime && shouldInitRuntime(lppInit)) {
          await createPokerRuntime({ forceScriptReload: forceRuntimeScriptReload })
        }

        return lppInit
      } catch (error) {
        loadingError.value = true
        runtimeReady.value = false
        throw error
      } finally {
        loading.value = false
        initPromise = null
      }
    })()

    return initPromise
  }

  watch(optionsResult, (value) => {
    if (!window.lpp) {
      return
    }

    window.lpp.updateOptions(value)
  })

  function updateOptions(payload) {
    options.value = Object.assign(options.value, payload)
  }

  return {
    init: initPoker,
    loading,
    loadingError,
    pokerObject: computed(() => pokerData.value),
    runtimeScriptError,
    runtimeReady,
    updateOptions,
  }
}
